import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { isNumber, get, isNil, isEmpty, isEqual, set, includes } from 'lodash';
import moment from 'moment';
import { IDatepickerConfig, IDatepickerValue, IMenuItem, IDatePeriod, IPeriod, IWeekPeriod } from '../types';
import { XUX_DATEPICKER_PERIODS } from '../datepicker-specific-constants';
import { ComponentStaticText } from '../components';
import { DateTypeModel } from '@xenial-ux/xux-datepicker/types/dateTypeModel';
import { TranslateService } from '@ngx-translate/core';

const defaultConfig: IDatepickerConfig = {
  required: true,
  availableOptions: [XUX_DATEPICKER_PERIODS.SINGLE],
  yearsRange: [moment().subtract('6', 'year').get('year'), moment().get('year')],
  dateFormat: {
    short_date_format: 'MM/DD/YYYY',
    long_date_format: 'MMM DD YYYY',
  },
};

@Component({
  selector: 'xux-datepicker',
  templateUrl: './xux-datepicker-layout.component.html',
  styleUrls: ['./xux-datepicker-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxDatepickerLayoutComponent implements OnInit {
  @Input('value') public defaultValue: IDatepickerValue;
  @Input('config') set _config(value: IDatepickerConfig) {
    const config = Object.assign({}, defaultConfig, value);
    const configMapper = get(value, 'configMapper');
    this.config = configMapper ? configMapper(config) : config;
  }
  @Input('periods') public set _periods(value: IPeriod[]) {
    const enableMinDateCheck = this.config.enableMinDateCheck;
    if(!enableMinDateCheck || isNil(this.config.minDate)) {
      this.periods = value;

      return;
    }

    this.periods = value.reduce((periods, period) => {
        const dateStart = moment(period.dateStart);

        const minDate = moment(this.config.minDate);

        if(minDate.isSameOrBefore(dateStart)) {
          return [...periods, period]
        }

        const dateEnd = moment(period.dateEnd);

        if(minDate.isBefore(dateEnd)) {
          return [...periods, {
            ...period,
            dateStart: minDate.toDate(),
          }]
        }

        return periods;
    }, []);
  }
  @Input() public weekPeriods?: IWeekPeriod[];
  @Input() public set i18n(staticTextContent: ComponentStaticText) {
    this.staticTextContent = {
      ...this.staticTextContent,
      ...(staticTextContent || {}),
    };
  }

  @Output() onApply: EventEmitter<IDatepickerValue> = new EventEmitter();
  @Output() onCancel: EventEmitter<IDatePeriod> = new EventEmitter();

  private periods: IPeriod[]

  public staticTextContent: ComponentStaticText = {
    selectDate: 'Select Date',
    date: 'Date',
    cancel: this.translations.instant('DATEPICKER.CANCEL'),
    apply: this.translations.instant('DATEPICKER.APPLY'),
  };
  public config: IDatepickerConfig = defaultConfig;
  public menuItems: IMenuItem[];
  public activeMenuItem: IMenuItem;
  public calendarValue: IDatePeriod; // setter for datepicker value
  public value: IDatePeriod;

  private isAutoTriggeredChangeFlag: boolean = false;
  private prevMenuItemType: string;

  constructor(
    private cd: ChangeDetectorRef,
    private translations: TranslateService,
  ) { }

  public ngOnInit(): void {
    this.menuItems = this.config.availableOptions.reduce((totalItems, item) => {
      // hidden periods when periods is empty
      if (isEmpty(this.periods) && isEqual(item.id, 'periods')) {
          return totalItems;
      }

      const menuItem = {
        ...item,
        name: this.translations.instant(`DATEPICKER.${item.name}`) || item.name
      }

      if (
        this.config.enableMinDateCheck &&
        !isNil(this.config.minDate) &&
        (includes([DateTypeModel.THIS_MONTH, DateTypeModel.THIS_YEAR],item.id))
      ) {
          const minDateMoment = moment(this.config.minDate);
          set(
              menuItem,
              'value.dateStart',
              minDateMoment.isSameOrBefore(menuItem?.value?.dateStart)
                  ? menuItem?.value?.dateStart
                  : this.config.minDate
          );
      }

      return [
          ...totalItems,
          menuItem,
      ];
    }, []);

    const defaultMenuItem = this.defaultValue?.type && this.menuItems.find(item => item.id === this.defaultValue.type);
    this.prevMenuItemType = defaultMenuItem?.type;
    this.selectMenuItem(defaultMenuItem || this.menuItems[0]);

    if (this.defaultValue?.value) {
      if (this.config.clearInvalidDateSelection && this.activeMenuItem.type === 'single') {
        const isValidDateSelected: boolean = this.isValidDateSelected(this.defaultValue.value, this.config.minDate, this.config.maxDate);
        this.calendarValue = isValidDateSelected ? this.defaultValue.value : { dateStart: null };
        this.value = isValidDateSelected ? this.defaultValue.value : { dateStart: null };
      } else {
        this.calendarValue = this.defaultValue.value;
      }
    }
  }

  public selectMenuItem(item: IMenuItem): void {
    this.activeMenuItem = item;
    if (
      item.id === DateTypeModel.CUSTOM && !(this.prevMenuItemType === DateTypeModel.PERIODS_WEEK && !this.value) ||
      item.id === DateTypeModel.CUSTOM && !(this.prevMenuItemType === DateTypeModel.PERIODS && !this.value)
    ) {
      const customValue = this.value || this.defaultValue.value;
      customValue.dateEnd = customValue.dateEnd || customValue.dateStart;
      this.defaultValue = {
        type: DateTypeModel.CUSTOM,
        value: Object.assign({}, customValue),
      };
      const calendarEndDate = this.prevMenuItemType === DateTypeModel.LATEST_BUSINESS_DAY
        ? this.defaultValue.value.dateStart
        : this.prevMenuItemType === DateTypeModel.PERIODS
          ? moment(customValue.dateEnd).subtract(1, 'd').toDate()
          : customValue.dateEnd;
      this.calendarValue = {
        dateStart: this.defaultValue.value.dateStart,
        dateEnd: calendarEndDate
      };
    } else if (this.isNotSameTypeOfItemAsPrevious(item)) {
      this.defaultValue = null;
      this.value = null;
    } else if (item.value) {
      this.calendarValue = Object.assign({}, item.value);
      this.isAutoTriggeredChangeFlag = true;
      this.setValue(item.value);
    }

    this.maySelectingCorrectPeriodsItem(item);
    this.prevMenuItemType = item.id;
  }

  public onAnyCalendarChange(period: IDatePeriod): void {
    if (!this.isAutoTriggeredChangeFlag && !this.isDefaultValueSelected()) {
      this.activeMenuItem = this.menuItems.find(item => item.id === DateTypeModel.CUSTOM);
    }
    if (this.config.setDateStartForCustomRange && !period.dateEnd) {
      this.value.dateStart = period.dateStart;
    }
  }

  public onCalendarChange(newValues: IDatePeriod): void {
    this.setValue(newValues);
    this.isAutoTriggeredChangeFlag = false;
    if (
      !this.isDefaultValueSelected() && this.activeMenuItem.id === DateTypeModel.LATEST_BUSINESS_DAY ||
      !this.isDefaultValueSelected() && this.activeMenuItem.id === DateTypeModel.THIS_MONTH ||
      !this.isDefaultValueSelected() && this.activeMenuItem.id === DateTypeModel.THIS_YEAR
    ) {
      this.activeMenuItem = this.menuItems.find(item => item.id === DateTypeModel.CUSTOM);
    }
  }

  public canApply(): boolean {
    return this.activeMenuItem && this.value && (
      this.activeMenuItem.type === 'single'
        ? !!this.value.dateStart
        : !!this.value.dateStart && !!this.value.dateEnd
    );
  }

  public apply(): void {
    const value: IDatepickerValue = {
      type: this.activeMenuItem.id,
      value: this.value,
    };
    this.onApply.emit(value);
  }

  public cancel(): void {
    this.onCancel.emit();
  }

  public setValue(newValue: IDatePeriod): void {
    this.value = Object.assign({}, newValue);
    this.cd.detectChanges();
  }

  public formatDate(date: Date, format: string): string {
    return date ? moment(date).format(format) : this.staticTextContent.selectDate as string;
  }

  private maySelectingCorrectPeriodsItem(item: IMenuItem): void {
    if (item.id === DateTypeModel.PREVIOUS_PERIOD) {
      if (!item.value) {
        const defaultValue = this.getRightPeriod(
          this.periods,
          DateTypeModel.PREVIOUS_PERIOD
        );
        this.activeMenuItem.value = defaultValue;
      }

      this.defaultValue = {
        type: DateTypeModel.PREVIOUS_PERIOD,
        value: this.activeMenuItem.value,
      };
      this.value = this.defaultValue.value;
    }

    if (item.id === DateTypeModel.CURRENT_PERIOD) {
      if (!item.value) {
        const defaultValue = this.getRightPeriod(
          this.periods,
          DateTypeModel.CURRENT_PERIOD
        );
        this.activeMenuItem.value = defaultValue;
      }

      this.defaultValue = {
        type: DateTypeModel.CURRENT_PERIOD,
        value: this.activeMenuItem.value,
      };
      this.value = this.defaultValue.value;
    }

    if (item.id === DateTypeModel.PERIODS) {
      if (!this.defaultValue?.value || !item.value) {
        const defaultValue = this.getRightPeriod(
          this.periods,
          DateTypeModel.CURRENT_PERIOD
        );
        this.activeMenuItem.value = defaultValue;
      } else {
        this.activeMenuItem.value = this.defaultValue.value;
      }

      this.defaultValue = {
        type: DateTypeModel.PERIODS,
        value: this.activeMenuItem.value,
      };
      this.value = this.defaultValue.value;
    }
  }

  private isDefaultValueSelected(): boolean {
    return this.activeMenuItem.value &&
      moment(this.activeMenuItem.value.dateStart).isSame(this.value.dateStart) &&
      moment(this.activeMenuItem.value.dateEnd).isSame(this.value.dateEnd);
  }

  private getRightPeriod(calendars: IPeriod[], type: string): IDatePeriod {
    let defaultValue;
    if (type === DateTypeModel.CURRENT_PERIOD || type === DateTypeModel.PERIODS) {
      defaultValue = calendars.find(item =>
        moment().isBetween(item.dateStart, moment(item.dateEnd).subtract(1, 'd'), 'day', '[]')
      );
    }

    if (type === DateTypeModel.PREVIOUS_PERIOD) {
      let index: number | null = null;
      calendars.forEach((item, i) => {
        if (moment().isBetween(item.dateStart, moment(item.dateEnd).subtract(1, 'd'), 'day', '[]')) {
          index = i - 1;
        }
      });

      if (isNumber(index)) {
        defaultValue = calendars[index];
      }
    }

    if (defaultValue) {
      defaultValue = {
        ...defaultValue,
      };
    } else {
      // if haven't added calendar periods for the current year will select a last available period
      defaultValue = {
        ...calendars[calendars.length - 1],
      };
    }

    return {
      dateStart: moment(defaultValue.dateStart).toDate(),
      dateEnd: moment(defaultValue.dateEnd).toDate(),
    };
  }

  private isValidDateSelected(valueToBeChecked: IDatePeriod, minDate: Date, maxDate: Date): boolean {
    let isValid = true;

    if (!valueToBeChecked) {
      return isValid;
    }

    if (minDate) {
      isValid = moment(valueToBeChecked.dateStart) >= moment(minDate).startOf('day');
    }

    if (isValid && maxDate) {
      isValid = moment(valueToBeChecked.dateStart) <= moment(maxDate).endOf('day');
    }

    return isValid;
  }

  private isNotSameTypeOfItemAsPrevious(item: IMenuItem): boolean {
    return (item.id === DateTypeModel.PERIODS && this.prevMenuItemType !== DateTypeModel.PERIODS) ||
      (item.id === DateTypeModel.PERIODS_WEEK && this.prevMenuItemType !== DateTypeModel.PERIODS_WEEK) ||
      (item.id === DateTypeModel.PREVIOUS_PERIOD && this.prevMenuItemType !== DateTypeModel.PERIODS) ||
      (item.id === DateTypeModel.CURRENT_PERIOD && this.prevMenuItemType !== DateTypeModel.PERIODS);
  }
}
